// XNGAGE CONFIDENTIAL
// __________________________
//
// Copyright (C) 2026 Xngage - All Rights Reserved
//
// All code or information contained herein is, and remains the
// property of Xngage LLC and its customers.  The intellectual
// and technical concepts contained are proprietary to Xngage LLC
// and may be covered by U.S. and Foreign Patents, patents in
// process, and are protected by trade secret or copyright law.
// Dissemination of this information or reproduction of this material
// is strictly forbidden unless prior written permission is obtained
// from Xngage LLC.
//

import { createHandlerChainRunner, Handler } from "@insite/client-framework/HandlerCreator";
import { getSettingsCollection } from "@insite/client-framework/Store/Context/ContextSelectors";
import { AnyAction } from "@insite/client-framework/Store/Reducers";
import { PaginationModel } from "@insite/client-framework/Types/ApiModels";
import { getProductCollectionV1 } from "@stoneage/App/Services/ProductServiceV1/ProductServiceV1";
import {
    getQuickProductLookup,
    QuickProductLookupBaseModel,
    QuickProductLookupModel,
} from "@stoneage/App/Services/QuickProductLookup/QuickProductLookupService";
import { setUrlsToQuickLookupProducts } from "@stoneage/App/Widgets/QuickProductLookup/QuickProductLookupHelpers";

interface Props {
    apiResult: {
        successful: boolean;
        products: QuickProductLookupModel | null;
        error?: string;
    };
}

type Parameter = {
    onSuccess?: (result: any) => void;
    onError?: (e: any) => void;
};

type HandlerType = Handler<Parameter, Props>;

export const BeginLoadingProducts: HandlerType = props => {
    props.dispatch({ type: "Pages/ProductLookup/BeginLoadingProducts" } as unknown as AnyAction);
};

export const RequestDataFromApi: HandlerType = async props => {
    props.apiResult = await getQuickProductLookup();
};

export const onSuccess: HandlerType = async props => {
    if (props.apiResult && props.apiResult.successful) {
        const defaultPageSize = getSettingsCollection(props.getState())?.websiteSettings.defaultPageSize;
        const { pagination, paginatedProducts } = await buildProductsPagination(
            // eslint-disable-next-line @typescript-eslint/no-non-null-asserted-optional-chain
            props.apiResult.products?.dealerPrices!,
            defaultPageSize,
        );
        props.dispatch({
            type: "Pages/ProductLookup/CompletedLoadingProducts",
            products: props.apiResult.products,
            filteredProducts: props.apiResult.products?.dealerPrices,
            paginatedProducts,
            pagination,
        } as unknown as AnyAction);
        props.parameter.onSuccess?.(props.apiResult);
    }
};

export const OnError: HandlerType = props => {
    if (props.apiResult && !props.apiResult.successful) {
        props.dispatch({
            type: "Pages/ProductLookup/CompletedLoadingProducts",
            products: null,
        } as unknown as AnyAction);
        props.parameter.onError?.(props.apiResult.error);
    }
};

async function buildProductsPagination(
    products: QuickProductLookupBaseModel[],
    defaultPageSize: number,
    page?: number,
) {
    const pageSizeOptions = [1, 2, 3, 4].map(val => val * defaultPageSize);
    const pagination = {
        currentPage: page || 1,
        page: page || 1,
        pageSize: defaultPageSize,
        defaultPageSize,
        totalItemCount: products?.length,
        numberOfPages: products?.length ? Math.ceil(products.length / defaultPageSize) : 0,
        pageSizeOptions,
        nextPageUri: "",
        prevPageUri: "",
    } as PaginationModel;
    const startingIndex = page && page > 1 ? defaultPageSize * page : 0;
    const _paginatedProducts = products.slice(startingIndex, defaultPageSize);
    const productNumbers =
        _paginatedProducts.filter(product => !product.urlIsSet).map(item => item.productNumber) || [];
    let paginatedProducts = [..._paginatedProducts];
    if (productNumbers.length !== 0) {
        try {
            const { products } = await getProductCollectionV1({ erpNumbers: productNumbers });
            paginatedProducts = setUrlsToQuickLookupProducts(products, _paginatedProducts);
        } catch (error: any) {
            /* empty */
        }
    }

    return {
        pagination,
        paginatedProducts,
    };
}

export const chain = [BeginLoadingProducts, RequestDataFromApi, onSuccess, OnError];

const getQuickProductLookups = createHandlerChainRunner(chain, "GetQuickProductLookups");
export default getQuickProductLookups;
